const NOTIFICATION_NAVIGATE_MESSAGE_TYPE = "msg.notification.navigate";
const NOTIFICATION_PENDING_REQUEST_MESSAGE_TYPE = "msg.notification.pending.request";
const NOTIFICATION_PENDING_ACK_MESSAGE_TYPE = "msg.notification.pending.ack";
const PENDING_REQUEST_REPLAY_DELAYS_MS = [0, 250, 1000, 2500] as const;
const TARGET_NAVIGATION_REPLAY_DELAYS_MS = [0, 100, 500, 1500] as const;
const PENDING_ACK_DELAY_MS = 2000;

interface ServiceWorkerMessengerLike {
  postMessage(message: unknown): void;
}

interface ServiceWorkerRegistrationLike {
  active?: ServiceWorkerMessengerLike | null;
}

interface ServiceWorkerMessageEventLike {
  data?: unknown;
  source?: ServiceWorkerMessengerLike;
}

interface ServiceWorkerContainerLike {
  addEventListener(type: "message", listener: (event: ServiceWorkerMessageEventLike) => void): void;
  removeEventListener?(type: "message", listener: (event: ServiceWorkerMessageEventLike) => void): void;
  controller?: ServiceWorkerMessengerLike | null;
  ready?: Promise<ServiceWorkerRegistrationLike>;
  startMessages?(): void;
}

interface NotificationNavigationLocationLike {
  href: string;
  origin: string;
  assign(url: string): void;
}

interface NotificationNavigationHistoryLike {
  replaceState?(data: unknown, unusedTitle: string, url?: string | URL | null): void;
  pushState?(data: unknown, unusedTitle: string, url?: string | URL | null): void;
}

interface NotificationNavigationRuntimeLike {
  location: NotificationNavigationLocationLike;
  history?: NotificationNavigationHistoryLike;
  dispatchEvent?(event: Event): boolean;
  addEventListener?(type: "focus" | "pageshow", listener: () => void): void;
  removeEventListener?(type: "focus" | "pageshow", listener: () => void): void;
  document?: {
    addEventListener?(type: "visibilitychange", listener: () => void): void;
    removeEventListener?(type: "visibilitychange", listener: () => void): void;
  };
  navigator?: {
    serviceWorker?: ServiceWorkerContainerLike;
  };
}

function trimOrEmpty(value: unknown): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim();
}

export function resolveServiceWorkerNotificationTarget(data: unknown, origin: string): string | null {
  if (typeof data !== "object" || data === null) {
    return null;
  }

  const record = data as Record<string, unknown>;
  const messageType = trimOrEmpty(record.type);
  if (messageType !== NOTIFICATION_NAVIGATE_MESSAGE_TYPE) {
    return null;
  }

  const candidate = trimOrEmpty(record.target_url || record.open_url);
  if (candidate === "") {
    return null;
  }

  try {
    const parsed = new URL(candidate, origin);
    if (parsed.origin !== origin) {
      return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

function dispatchPopState(runtime: NotificationNavigationRuntimeLike): void {
  if (typeof runtime.dispatchEvent !== "function") {
    return;
  }
  try {
    runtime.dispatchEvent(new PopStateEvent("popstate"));
    return;
  } catch {
    // Fallback for runtimes that do not expose PopStateEvent constructor.
  }

  try {
    runtime.dispatchEvent(new Event("popstate"));
  } catch {
    // Ignore unsupported event constructors.
  }
}

export function navigateRuntimeToNotificationTarget(
  runtime: NotificationNavigationRuntimeLike,
  target: string,
): void {
  const history = runtime.history;
  if (history !== undefined) {
    try {
      if (typeof history.replaceState === "function") {
        history.replaceState(null, "", target);
        dispatchPopState(runtime);
        return;
      }
      if (typeof history.pushState === "function") {
        history.pushState(null, "", target);
        dispatchPopState(runtime);
        return;
      }
    } catch {
      // Fall through to location.assign for unsupported history mutations.
    }
  }

  runtime.location.assign(target);
}

export function setupServiceWorkerNotificationNavigationBridge(
  runtime: NotificationNavigationRuntimeLike = window,
): () => void {
  const serviceWorker = runtime.navigator?.serviceWorker;
  if (serviceWorker === undefined || typeof serviceWorker.addEventListener !== "function") {
    return () => {};
  }

  let stopped = false;
  const scheduledTimers: Array<ReturnType<typeof setTimeout>> = [];

  const schedule = (delayMs: number, task: () => void) => {
    if (delayMs <= 0) {
      if (!stopped) {
        task();
      }
      return;
    }

    const timer = setTimeout(() => {
      if (!stopped) {
        task();
      }
    }, delayMs);
    scheduledTimers.push(timer);
  };

  const postMessageToServiceWorker = (payload: unknown) => {
    try {
      serviceWorker.controller?.postMessage(payload);
    } catch {
      // Ignore unsupported controller postMessage calls.
    }
    const ready = serviceWorker.ready;
    if (ready !== undefined) {
      void ready
        .then((registration) => {
          if (!stopped) {
            registration?.active?.postMessage(payload);
          }
        })
        .catch(() => {
          // Ignore ready/active failures when runtime is still bootstrapping.
        });
    }
  };

  const acknowledgePendingNavigation = (target: string) => {
    postMessageToServiceWorker({
      type: NOTIFICATION_PENDING_ACK_MESSAGE_TYPE,
      target_url: target,
    });
  };

  const replayNotificationTarget = (target: string) => {
    for (const delayMs of TARGET_NAVIGATION_REPLAY_DELAYS_MS) {
      schedule(delayMs, () => {
        navigateRuntimeToNotificationTarget(runtime, target);
      });
    }
    schedule(PENDING_ACK_DELAY_MS, () => {
      acknowledgePendingNavigation(target);
    });
  };

  const onMessage = (event: ServiceWorkerMessageEventLike) => {
    const target = resolveServiceWorkerNotificationTarget(event.data, runtime.location.origin);
    if (target === null) {
      return;
    }

    replayNotificationTarget(target);
  };

  const requestPendingNavigationOnce = (allowLatestPushTarget: boolean) => {
    const requestPayload = {
      type: NOTIFICATION_PENDING_REQUEST_MESSAGE_TYPE,
      allow_latest_push_target: allowLatestPushTarget,
    };
    postMessageToServiceWorker(requestPayload);
  };

  const requestPendingNavigation = (allowLatestPushTarget: boolean) => {
    for (const delayMs of PENDING_REQUEST_REPLAY_DELAYS_MS) {
      schedule(delayMs, () => {
        requestPendingNavigationOnce(allowLatestPushTarget);
      });
    }
  };

  const onFocus = () => {
    requestPendingNavigation(true);
  };
  const onPageShow = () => {
    requestPendingNavigation(true);
  };
  const onVisibilityChange = () => {
    requestPendingNavigation(true);
  };

  serviceWorker.addEventListener("message", onMessage);
  try {
    serviceWorker.startMessages?.();
  } catch {
    // Ignore unsupported explicit message starts.
  }
  runtime.addEventListener?.("focus", onFocus);
  runtime.addEventListener?.("pageshow", onPageShow);
  runtime.document?.addEventListener?.("visibilitychange", onVisibilityChange);
  requestPendingNavigation(false);

  return () => {
    stopped = true;
    for (const timer of scheduledTimers) {
      clearTimeout(timer);
    }
    if (typeof serviceWorker.removeEventListener === "function") {
      serviceWorker.removeEventListener("message", onMessage);
    }
    runtime.removeEventListener?.("focus", onFocus);
    runtime.removeEventListener?.("pageshow", onPageShow);
    runtime.document?.removeEventListener?.("visibilitychange", onVisibilityChange);
  };
}
